import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';
import Colors from '../utilities/Colors';
import { d2gRound } from '../utilities/Utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { onHoverHistogram, onClickHistogram, onSetActiveIndicator, useGlobalActiveGeography, onSetActiveRGModuleId, onSetActiveDBModuleId, onSetActiveSTModuleId, onSetActiveModuleId, useGlobalActivePage } from '../../data/StatusStore';
import { useGlobalSwarmCalibration } from '../../data/GlobalStore';

interface BaseDataItem {
  id: string;
  value: number;
}

interface RangeDataItem extends BaseDataItem {
  x: number;
  y: number;
  next: RangeDataItem | null;
}

interface DataItem {
  id: string;
  value: number;
  x?: number;
  y?: number;
  tooltip?: any;
}

interface RangeSwarmStaticProps {
  dataArray: BaseDataItem[];
  histMax: number;
  histMin: number;
  histNA: number;
  histLength: number;
  chartId: string;
  nodes: DataItem[];
  setNodes: Function;
  indicator: string;
  colorClass: string;
  DESCRIPTOR: string;
  labelOnly?: any;
  allData: any;
  options?: any;
  context: string;
  preppedData: any;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const RangeSwarmStatic: React.FC<RangeSwarmStaticProps> = ({
  dataArray,
  chartId,
  histMax,
  histMin,
  histNA,
  histLength,
  nodes,
  setNodes,
  indicator,
  colorClass,
  DESCRIPTOR,
  labelOnly,
  allData,
  options,
  context,
  preppedData,
}) => {
  const rg = context === "race and gender" ? "RG" : "";
  const swarmCalibration = useGlobalSwarmCalibration();
  const activeGeography = useGlobalActiveGeography();
  const activePage = useGlobalActivePage();

  const width = "100%";
  const height = "100";
  const [axisLabels, setAxisLabels] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [collisionDiameter, setCollisionDiameter] = useState<number>(swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.collisionDiameter ?? 0.25);
  const [pointDiameter, setPointDiameter] = useState<number>((swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.radius ?? 1) * 2);
  const [padding, setPadding] = useState<number>(swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.padding ?? 0.25);
  const [radius, setRadius] = useState<number>(swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.radius ?? 1);
  const [segmentLengthPct, setSegmentLengthPct] = useState<number>(swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.segmentLengthPct ?? 6);
  const [intervalBufferPct, setIntervalBufferPct] = useState<number>(swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.intervalBufferPct ?? 1.5);
  const [rowSpacing, setRowSpacing] = useState<number>(swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`]?.rowSpacing ?? 0);

  function calculateSwarmPositions(data: DataItem[], min: number, max: number, plotWidth: number, segmentLengthPct: number, pointDiameter: number, collisionDiameter: number, intervalBufferPct: number, rowSpacing: number, stackCount: number, _testEachPoint: boolean, allData: any, labelOnly: any): DataItem[] {
    const radiusLocal = pointDiameter / 2;
    const collisionRadius = collisionDiameter;
    let lastPoint: DataItem | null = null;
    let direction = 1;
    let run = 0;
    const tooltipVar = labelOnly?.length > 0 ? labelOnly.filter((item: any) => item.sort.Tooltip_only === indicator) : null;

    data.forEach((item) => {
      item.tooltip = null;
      if (tooltipVar && tooltipVar[0]) {
        item.tooltip = allData.dataJSON[item.id][labelOnly[0].variable];
      }

      item.x = (((item.value - min) / (max - min)) * (plotWidth - segmentLengthPct)) + (segmentLengthPct / 2);
      if (isNaN(item.x) || item.x === undefined || item.value.toString() === "NA") {
        item.x = -2;
      }
      item.y = 0;

      const radiusMultiplier = context === "race and gender"
        ? activeGeography === "CD" ? 7.5 : 8
        : 2;
      const intervalCollision = segmentLengthPct + intervalBufferPct;

      if (item.value.toString() !== "NA") {
        if (lastPoint && Math.abs(lastPoint.x! - item.x!) < Math.max(collisionRadius * radiusMultiplier, intervalCollision)) {
          item.y = (run * (radiusLocal * radiusMultiplier + rowSpacing)) * direction;
          if (direction === 1) {
            run = run + 1;
          }
          direction *= -1;
        } else {
          direction = 1;
          run = 0;
          if (!_testEachPoint) {
            lastPoint = item;
          }
        }
        if (Math.abs(item.y) > stackCount) {
          run = 0;
          lastPoint = item;
        }
      }

      if (_testEachPoint) {
        lastPoint = item;
      }
    });

    return data;
  }

  interface AxisLabel {
    tickValue: number;
    width: string;
  }

  function computeAxisLabels(min: number, max: number): AxisLabel[] {
    const range = max - min;
    const mid = min + range / 2;
    const ticks = [min, mid, max];
    const labels: AxisLabel[] = ticks.map((tick: number, index: number) => {
      let segmentWidth = "0%";
      if (index < ticks.length - 1) {
        segmentWidth = ((ticks[index + 1] - tick) / range * 100).toFixed(2) + "%";
      }
      return { tickValue: tick, width: segmentWidth };
    });
    labels[labels.length - 1].width = "0%";
    return labels;
  }

  useEffect(() => {
    const calibration = swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`] ?? swarmCalibration?.[`${activeGeography}${rg}`];
    const _collisionDiameter = calibration?.collisionDiameter ?? 0.25;
    const _radius = calibration?.radius ?? 1;
    const _pointDiameter = _radius * 2;
    const _padding = calibration?.padding ?? 0.25;
    const _stackCount = calibration?.stackCount ?? 35;
    const _testEachPoint = calibration?.testEachPoint === "TRUE" ? true : false;
    const _segmentLengthPct = calibration?.segmentLengthPct ?? 6;
    const _intervalBufferPct = calibration?.intervalBufferPct ?? 1.5;
    const _rowSpacing = calibration?.rowSpacing ?? 0;

    setCollisionDiameter(_collisionDiameter);
    setPointDiameter(_pointDiameter);
    setRadius(_radius);
    setPadding(_padding);
    setSegmentLengthPct(_segmentLengthPct);
    setIntervalBufferPct(_intervalBufferPct);
    setRowSpacing(_rowSpacing);

    const swarmData = calculateSwarmPositions(dataArray.map((item: any) => ({ ...item })), histMin, histMax, 100, _segmentLengthPct, _pointDiameter, _collisionDiameter, _intervalBufferPct, _rowSpacing, _stackCount, _testEachPoint, allData, labelOnly);
    setNodes(swarmData);
    setAxisLabels(computeAxisLabels(histMin, histMax));
    setIsLoading(false);
  }, [dataArray, height, width, histMax, histMin, histLength, swarmCalibration, activeGeography]);

  return (
    <div key={`svg_range_swarm_static_container_${chartId}`} className='swarm-container'>
      {isLoading ? (
        <div key={`svg_range_swarm_static_spinner_${chartId}`} className="loading-spinner">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      ) : (
        <svg
          key={`svg_range_swarm_static_${chartId}`}
          width={width}
          height={height}
          className="swarm-chart-static"
          style={{ overflow: 'visible' }}
          onMouseLeave={() => onHoverHistogram(null)}
        >
          {nodes.map((node, i) => {
            if ((node.x ?? -2) < 0) return null;
            let color = "#000";
            if (preppedData) {
              color = Colors.getColorQuintile(preppedData.rankedData[indicator][node.id].index, preppedData.countNA, preppedData.countALL, colorClass);
            } else {
              color = Colors.getColorQuintile(i, histNA, histLength, colorClass);
            }

            const yPct = node.y
              ? `${((parseInt(height) / 2 - radius - padding - node.y) / parseInt(height)) * 100}%`
              : `${((parseInt(height) / 2 - radius - padding) / parseInt(height)) * 100}%`;
            const xCenter = node.x ?? 0;
            const halfSegment = segmentLengthPct / 2;
            const x1 = `${xCenter - halfSegment}%`;
            const x2 = `${xCenter + halfSegment}%`;

            return (
              <line
                key={`${node.id}_${chartId}_${i}`}
                x1={x1}
                x2={x2}
                y1={yPct}
                y2={yPct}
                stroke={color}
                strokeWidth={Math.max(2, radius * 2)}
                strokeLinecap="round"
                onMouseEnter={() => onHoverHistogram(node.id)}
                onMouseLeave={() => onHoverHistogram(null)}
                onClick={() => onClickHistogram(node.id, "swarm", options.variables[0].sort.moduleId)}
              />
            );
          })}
          <line
            x1={`${segmentLengthPct / 2}%`}
            x2={`${100 - segmentLengthPct / 2}%`}
            y1="50%"
            y2="50%"
            stroke="#D7C8C8"
            strokeWidth="1"
            style={{ pointerEvents: 'none' }}
          />
        </svg>
      )}
    </div>
  );
};

export default RangeSwarmStatic;